import type { Instance } from "@gadgetinc/mobx-quick-tree";
import { toaster } from "baseui/toast";
import React, { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { EnvironmentSlug, EnvironmentStatus, EnvironmentType } from "state-trees/src/Environment";
import { CombinedError, gql } from "urql";
import {
  EnvironmentStatus as GraphQLEnvironmentStatus,
  EnvironmentType as GraphQLEnvironmentType,
  useListAllEnvironmentsQuery,
} from "../../../__generated__/edit-graphql";
import type { ClientApplicationRoot } from "../../../client-state-trees/workspace/ClientApplicationRoot";
import type { ClientEnvironmentRoot } from "../../../client-state-trees/workspace/ClientEnvironmentRoot";

gql`
  query ListAllEnvironments {
    environments {
      id
      slug
      name
      lastUserEditDate
      type
      status
      usingGadgetAuthentication
    }
  }
`;

export const ApplicationRootContext = createContext<ClientApplicationRoot>(null as any);
export const useCurrentApplication = () => useContext(ApplicationRootContext);

export const CurrentEnvironmentRootContext = createContext<Instance<typeof ClientEnvironmentRoot>>(null as any);
export const useCurrentEnvironment = () => useContext(CurrentEnvironmentRootContext);
export const useCurrentEnvironmentSettings = () => useCurrentEnvironment().settings.getOrSuspend();
export const useCurrentEnvironmentIsPending = () => useCurrentEnvironmentSettings().environmentStatus == EnvironmentStatus.PENDING;

export const ProductionEnvironmentRootContext = createContext<Instance<typeof ClientEnvironmentRoot>>(null as any);
export const useProductionEnvironment = () => useContext(ProductionEnvironmentRootContext);
export const useProductionEnvironmentSettings = () => useProductionEnvironment().settings.getOrSuspend();

export interface AvailableEnvironment {
  id: string;
  name: string;
  slug: EnvironmentSlug;
  lastUserEditDate: string;
  type: EnvironmentType;
  status: EnvironmentStatus;
  usingGadgetAuthentication: boolean | null;
}

interface AvailableEnvironmentsContextValue {
  environments: AvailableEnvironment[];
  refetch: () => void;
  fetching: boolean;
  error: CombinedError | undefined;
}

export const AvailableEnvironmentsContext = createContext<AvailableEnvironmentsContextValue>({
  environments: [],
  refetch: () => {},
  fetching: false,
  error: undefined,
});
export const useAvailableEnvironments = () => useContext(AvailableEnvironmentsContext);

export const AvailableEnvironmentsProvider = (props: { children: ReactNode }) => {
  const [{ data, fetching, error }, refetch] = useListAllEnvironmentsQuery({
    requestPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (error) {
      toaster.negative("Error connecting to Gadget, please refresh the page and try again", { autoHideDuration: 3000 });
    }
  }, [error]);

  const environments: AvailableEnvironment[] = useMemo(() => {
    return (data?.environments ?? []).map((e) => ({
      id: e.id,
      name: e.name,
      slug: e.slug as EnvironmentSlug,
      lastUserEditDate: e.lastUserEditDate,
      type:
        e.type === GraphQLEnvironmentType.Production
          ? EnvironmentType.Production
          : e.type === GraphQLEnvironmentType.Test
          ? EnvironmentType.Test
          : EnvironmentType.Development,
      status:
        e.status === GraphQLEnvironmentStatus.Active
          ? EnvironmentStatus.ACTIVE
          : e.status === GraphQLEnvironmentStatus.Pending
          ? EnvironmentStatus.PENDING
          : e.status === GraphQLEnvironmentStatus.FatalError
          ? EnvironmentStatus.FATAL_ERROR
          : EnvironmentStatus.PAUSED,
      usingGadgetAuthentication: e.usingGadgetAuthentication ?? null,
    }));
  }, [data]);

  return (
    <AvailableEnvironmentsContext.Provider value={{ environments, refetch, fetching, error }}>
      {props.children}
    </AvailableEnvironmentsContext.Provider>
  );
};
